import type { z } from 'zod'
import { NetworkError } from '~/shared/lib/rpc'

export type FieldMessage = (issue: { code: string }) => string

/**
 * Validate an outgoing request payload against the shared request schema - the
 * same source of truth the backend enforces - before sending. The form's field
 * validators only run on touched fields, so untouched-but-invalid data (e.g. an
 * imported entity without a sub-category or address) would otherwise slip
 * through and return an opaque 400. Surface it client-side with field-mapped,
 * translated messages instead.
 *
 * `fieldMessages` maps a dotted issue path (e.g. `tags.subCategoryTags`) to a
 * paraglide message getter; unmapped paths fall back to the raw Zod message.
 * The getter receives the issue so a path with more than one rule (e.g.
 * `endTime`: required vs. after-start) can pick the right message.
 */
export function assertValidPayload(
  schema: z.ZodType,
  payload: unknown,
  fieldMessages: Record<string, FieldMessage>,
): void {
  const result = schema.safeParse(payload)
  if (result.success) return
  const messages = new Set<string>()
  for (const issue of result.error.issues) {
    // Strip a trailing array index (e.g. "tags.subCategoryTags.0") so the
    // field-message map still matches the field.
    const path = issue.path.join('.').replace(/\.\d+$/, '')
    messages.add(fieldMessages[path]?.(issue) ?? issue.message)
  }
  throw new NetworkError('BAD_REQUEST', 400, [...messages].join(' · '))
}
